import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CityService } from '@city/feature';
import { CustomProduct } from '@product/const';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ProductListAnalyticsService } from './product-list-analytics.service';

@Injectable()
export class CarouselAnalyticsComponentService extends ProductListAnalyticsService {
  private products$: Observable<CustomProduct[]>;
  private productsSnapshot: CustomProduct[] = [];
  private visibleProducts$: Observable<CustomProduct[]>;
  private offset$ = new BehaviorSubject<number>(0);
  private slidesPerView$ = new Subject<number>();
  private shownProductsIds$ = new Subject<string[]>();
  private intersected$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  constructor(cityService: CityService, injector: Injector) {
    super(injector);

    cityService.activeBaseStoreCode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.shownProductsIds$.next([]);
    });
  }

  setAnalyticsListName(name: string): void {
    this.analyticsListName$.next(name);
  }

  init(name: string | undefined, items$: Observable<CustomProduct[]>): void {
    this.products$ = items$;

    items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((products) => (this.productsSnapshot = products));

    this.visibleProducts$ = combineLatest({
      products: this.products$,
      offset: this.offset$,
      slidesPerView: this.slidesPerView$,
    }).pipe(map(({ products, offset, slidesPerView }) => products.slice(offset, offset + slidesPerView)));

    if (name) {
      this.setAnalyticsListName(name);
    }

    this.handleVisibleProducts();
    this.setOffset(0);
    this.shownProductsIds$.next([]);
  }

  override getProductIndex(productCode: string): number | undefined {
    const index = this.productsSnapshot.findIndex((product) => product.code === productCode);

    return index >= 0 ? index : undefined;
  }

  setOffset(offset: number): void {
    this.offset$.next(offset);
  }

  setSlidesPerView(slidesPerView: number): void {
    this.slidesPerView$.next(slidesPerView);
  }

  setIntersected(): void {
    this.intersected$.next();
  }

  private handleVisibleProducts(): void {
    combineLatest({
      visibleProducts: this.visibleProducts$,
      shownProductsIds: this.shownProductsIds$,
      intersected: this.intersected$.pipe(take(1)),
      offset: this.offset$,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ visibleProducts, shownProductsIds, offset }) => {
        const productsForAnalytics = visibleProducts.filter(
          (item: CustomProduct) => shownProductsIds.indexOf(item.code) === -1,
        );

        if (productsForAnalytics.length > 0) {
          this.shownProductsIds$.next([...shownProductsIds, ...visibleProducts.map(({ code }) => code)]);
          this.onViewProductList(productsForAnalytics, offset);
        }
      });
  }
}
